import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  useTheme,
  Chip,
  Stack,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Analytics,
  Security,
  Cloud,
  SupportAgent,
  CheckCircle,
  ArrowForward,
  Storage,
  NetworkCheck,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import CTASection from '../components/common/CTASection';

const ManagedServices = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const services = [
    { icon: <NetworkCheck />, title: 'Network Management', desc: '24/7 monitoring, configuration, and optimization' },
    { icon: <Security />, title: 'Security Operations', desc: 'Round-the-clock threat detection and response' },
    { icon: <Cloud />, title: 'Cloud Management', desc: 'Multi-cloud orchestration and cost optimization' },
    { icon: <SupportAgent />, title: 'IT Help Desk', desc: 'Multi-lingual support for global workforce' },
    { icon: <Storage />, title: 'Backup & Recovery', desc: 'Automated backups and disaster recovery' },
    { icon: <Analytics />, title: 'Performance Monitoring', desc: 'Real-time analytics and reporting' },
  ];

  return (
    <Box>
      <PageHeader 
        title="Managed Services" 
        subtitle="We handle your IT infrastructure so you can focus on your business"
      />

      {/* Stats */}
      <Box sx={{ py: 6, bgcolor: 'background.default' }}>
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            {[
              { number: '99.9%', label: 'Resolution Rate', color: '#0066CC' },
              { number: '<15min', label: 'Response Time', color: '#00A3FF' },
              { number: '500+', label: 'Certified Engineers', color: '#00C853' },
              { number: '24/7', label: 'Support Available', color: '#FF6B00' },
            ].map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: stat.color }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Grid */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Chip 
                  label="Managed Services" 
                  sx={{ mb: 2, bgcolor: theme.palette.primary.light + '20', color: theme.palette.primary.main }}
                />
                <Typography variant="h2" sx={{ mb: 3, fontWeight: 700 }}>
                  Focus on Your Core Business
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Let our certified experts manage your IT infrastructure while you focus on 
                  what matters most - growing your business.
                </Typography>
                <Grid container spacing={2}>
                  {services.map((service, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Stack direction="row" spacing={2}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>{service.icon}</Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {service.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {service.desc}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop"
                  alt="Managed Services"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: theme.shadows[8],
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <CTASection 
        title="Outsource Your IT" 
        subtitle="Let our experts manage your infrastructure with enterprise-grade SLAs"
        buttonText="Start Free Assessment"
        buttonLink="/contact"
      />
    </Box>
  );
};

export default ManagedServices;